import React, { useState } from "react";
import { Link } from "react-router-dom";
import { RxCross2 } from "react-icons/rx";
import type { FunctionErrorRow } from "../types/function-error.entity";
import { useTheme } from "src/context/theme.context";

type Props = {
  data: FunctionErrorRow[];
  showUserLink?: boolean;
  messageModalPosition?: "fixed" | "absolute";
};

const severityBadgeClass = (severity: string, isDark: boolean) => {
  const s = (severity || "").toLowerCase();
  if (isDark) {
    if (s === "error" || s === "critical") return "bg-red-900/30 text-red-300 border border-red-700/50";
    if (s === "warn" || s === "warning") return "bg-amber-900/30 text-amber-300 border border-amber-700/50";
    if (s === "info") return "bg-blue-900/30 text-blue-300 border border-blue-700/50";
    return "bg-gray-700/50 text-gray-300 border border-gray-600/50";
  }
  if (s === "error" || s === "critical") return "bg-red-50 text-red-700 border border-red-200";
  if (s === "warn" || s === "warning") return "bg-amber-50 text-amber-700 border border-amber-200";
  if (s === "info") return "bg-blue-50 text-blue-700 border border-blue-200";
  return "bg-gray-50 text-gray-700 border border-gray-200";
};

export const FunctionErrorsTable: React.FC<Props> = ({
  data,
  showUserLink = true,
  messageModalPosition = "fixed",
}) => {
  const { isDarkMode } = useTheme();
  const [selectedError, setSelectedError] = useState<FunctionErrorRow | null>(null);

  const tableWrap = isDarkMode
    ? "border border-gray-700 divide-y divide-gray-700"
    : "border border-gray-200 divide-y divide-gray-200";
  const thead = "bg-gray-900 text-white text-center";
  const rowBg = isDarkMode
    ? "hover:bg-gray-800/50"
    : "odd:bg-white even:bg-gray-50 hover:bg-gray-100";
  const borderT = isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200";
  const cellClass = isDarkMode ? "text-gray-200" : "text-gray-800";
  const mutedClass = isDarkMode ? "text-gray-400" : "text-gray-500";
  const emptyText = isDarkMode ? "text-gray-400" : "text-gray-500";
  const userLinkClass = isDarkMode
    ? "flex items-center gap-2 p-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/30 hover:shadow-md hover:from-amber-500/20 hover:to-orange-500/20 hover:border-amber-400/50 transition-all duration-200 cursor-pointer group no-underline"
    : "flex items-center gap-2 p-2 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200 hover:shadow-md hover:from-amber-100 hover:to-orange-100 hover:border-amber-300 transition-all duration-200 cursor-pointer group no-underline";
  const userBadgeClass = "flex items-center justify-center px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wide group-hover:from-amber-600 group-hover:to-orange-600 transition-all duration-200";
  const userTextClass = isDarkMode
    ? "text-[9px] sm:text-[10px] font-mono font-bold text-amber-300 group-hover:text-amber-100 transition-colors duration-200 break-all"
    : "text-[9px] sm:text-[10px] font-mono font-bold text-amber-700 group-hover:text-amber-900 transition-colors duration-200 break-all";
  const messageButtonClass = isDarkMode
    ? "w-full rounded-lg px-2 py-1 -mx-2 -my-1 text-left text-gray-200 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors"
    : "w-full rounded-lg px-2 py-1 -mx-2 -my-1 text-left text-gray-800 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-white transition-colors";
  const modalPositionClass = messageModalPosition === "absolute" ? "absolute" : "fixed";
  const modalOverlayClass = isDarkMode
    ? `${modalPositionClass} inset-0 z-50 flex items-center justify-center bg-black/70 p-4`
    : `${modalPositionClass} inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4`;
  const modalCardClass = isDarkMode
    ? "w-full max-w-3xl rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
    : "w-full max-w-3xl rounded-2xl border border-gray-200 bg-white shadow-2xl";
  const modalHeaderClass = isDarkMode
    ? "border-b border-gray-700 bg-gray-800/80"
    : "border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50";
  const modalTitleClass = isDarkMode ? "text-gray-100" : "text-gray-900";
  const modalMetaClass = isDarkMode ? "text-gray-400" : "text-gray-600";
  const modalBodyClass = isDarkMode
    ? "max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-xl border border-gray-700 bg-gray-950 p-4 font-mono text-sm text-gray-200"
    : "max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-xl border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-800";
  const closeButtonClass = isDarkMode
    ? "rounded-lg p-2 text-gray-400 hover:bg-gray-800 hover:text-gray-200 transition-colors"
    : "rounded-lg p-2 text-gray-500 hover:bg-white hover:text-gray-800 transition-colors";
  const footerButtonClass = isDarkMode
    ? "rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-white transition-colors"
    : "rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors";

  return (
    <>
      <div className={`overflow-x-auto mb-6 ${tableWrap} rounded-xl overflow-hidden`}>
        <table className="min-w-[700px] w-full text-[11px] sm:text-xs">
          <thead className={thead}>
            <tr>
              <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider text-left">
                Function
              </th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider text-left min-w-[200px]">
                Message
              </th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider text-left">
                Severity
              </th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">
                Code
              </th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider text-center">
                Count
              </th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider text-left">
                First / Last seen
              </th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className={`px-4 py-10 text-center text-sm ${emptyText}`}
                >
                  No function errors recorded.
                </td>
              </tr>
            ) : (
              data.map((err) => (
                <tr key={err.id} className={`${rowBg} transition-colors`}>
                  <td className={`px-2 py-2 sm:px-4 sm:py-3 font-mono text-xs ${cellClass} ${borderT}`}>
                    <div className="flex flex-col gap-2">
                      <span className="font-semibold tracking-wide">{err.functionName}</span>
                      {showUserLink && err.userId && (
                        <Link
                          to={`/users/${err.userId}`}
                          className={userLinkClass}
                          title={`View User: ${err.userId}`}
                        >
                          <div className={userBadgeClass}>
                            ID
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={userTextClass}>
                              {err.userId}
                            </div>
                          </div>
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className={`px-2 py-2 sm:px-4 sm:py-3 max-w-xs ${cellClass} ${borderT}`}>
                    <button
                      type="button"
                      className={messageButtonClass}
                      title="Open full error message"
                      onClick={() => setSelectedError(err)}
                    >
                      <span className="block truncate">{err.message}</span>
                    </button>
                  </td>
                  <td className={`px-2 py-2 sm:px-4 sm:py-3 ${borderT}`}>
                    <span
                      className={`inline-block px-2 py-1 rounded-full border text-xs font-semibold ${severityBadgeClass(
                        err.severity,
                        isDarkMode
                      )}`}
                    >
                      {err.severity || "—"}
                    </span>
                  </td>
                  <td className={`px-2 py-2 sm:px-4 sm:py-3 text-center ${cellClass} ${borderT}`}>
                    {err.code ?? "—"}
                  </td>
                  <td className={`px-2 py-2 sm:px-4 sm:py-3 text-center font-medium ${cellClass} ${borderT}`}>
                    {err.count}
                  </td>
                  <td className={`px-2 py-2 sm:px-4 sm:py-3 text-xs ${mutedClass} ${borderT}`}>
                    <div className="flex flex-col leading-tight">
                      <span>
                        {err.firstSeenAt
                          ? new Date(err.firstSeenAt).toLocaleString()
                          : "—"}
                      </span>
                      <span>
                        {err.lastSeenAt
                          ? new Date(err.lastSeenAt).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedError && (
        <div
          className={modalOverlayClass}
          onClick={() => setSelectedError(null)}
          role="presentation"
        >
          <div
            className={modalCardClass}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="function-error-message-title"
          >
            <div className={`flex items-start justify-between gap-4 px-6 py-5 ${modalHeaderClass}`}>
              <div className="min-w-0">
                <h3
                  id="function-error-message-title"
                  className={`text-lg font-semibold ${modalTitleClass}`}
                >
                  Full error message
                </h3>
                <p className={`mt-1 text-sm ${modalMetaClass}`}>
                  {selectedError.functionName}
                </p>
              </div>
              <button
                type="button"
                className={closeButtonClass}
                onClick={() => setSelectedError(null)}
                aria-label="Close full error message"
              >
                <RxCross2 className="text-xl" />
              </button>
            </div>

            <div className="px-6 py-5">
              <div className={modalBodyClass}>{selectedError.message}</div>
            </div>

            <div className="flex justify-end px-6 pb-6">
              <button
                type="button"
                className={footerButtonClass}
                onClick={() => setSelectedError(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
